import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  LocalHospital,
  MonitorHeart,
  Insights,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { crescer } from '../theme';

// Pontos do painel de marca (Crescer Design System — .crescer-login__points)
const PONTOS = [
  { icon: LocalHospital, titulo: 'Indicadores da APS', texto: 'Metas, equipes e listas nominais do município.' },
  { icon: MonitorHeart, titulo: 'Monitoramento integrado', texto: 'Leitos, filas, regulação e vigilância em um só lugar.' },
  { icon: Insights, titulo: 'Inteligência aplicada', texto: 'BI e IA para decidir com dados.' },
];

const labelSx = { fontSize: '0.71875rem', fontWeight: 600, color: crescer.textSoft, mb: 0.625 };

const ModernLogin: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setLoading(true);
    try {
      await login(email.trim(), password);
      setError('');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error;
      setError(msg && typeof msg === 'string' ? msg : 'Credenciais inválidas. Verifique seu e-mail e senha.');
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const nomePlataforma = branding.branding_nome || 'NeoGestor';
  const subtitulo = branding.branding_subtitulo || 'Plataforma de Governança de Indicadores';

  const marca = (tamanho: number, sobreGradiente: boolean) =>
    branding.branding_logo ? (
      <Box
        component="img"
        src={branding.branding_logo}
        alt={nomePlataforma}
        sx={{ width: tamanho, height: tamanho, objectFit: 'contain', borderRadius: '12px', flexShrink: 0 }}
      />
    ) : (
      <Box
        sx={{
          width: tamanho,
          height: tamanho,
          flexShrink: 0,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          ...(sobreGradiente
            ? { background: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.28)' }
            : { background: crescer.aiGrad, boxShadow: '0 4px 14px rgba(0,122,240,.35)' }),
        }}
      >
        <Typography sx={{ fontSize: tamanho * 0.4, fontWeight: 700, color: 'inherit', lineHeight: 1 }}>
          {nomePlataforma.substring(0, 2).toUpperCase()}
        </Typography>
      </Box>
    );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: crescer.bg,
        px: 2,
        py: 4,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: 440, md: 960 },
          minHeight: { md: 560 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.02fr 1fr' },
          backgroundColor: crescer.surface,
          border: `1px solid ${crescer.border}`,
          borderRadius: '18px',
          overflow: 'hidden',
          boxShadow: crescer.shadowModal,
        }}
      >
        {/* Painel de marca (esquerda) — some no mobile */}
        <Box
          component="aside"
          sx={{
            position: 'relative',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            px: 4.75,
            py: 4.25,
            color: 'white',
            background: 'linear-gradient(150deg, #007AF0 0%, #3f5fe6 46%, #6D4AE0 100%)',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -80,
              right: -80,
              width: 320,
              height: 320,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,.20), transparent 68%)',
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -120,
              left: -60,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,.10), transparent 70%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.375 }}>
            {marca(44, true)}
            <Typography sx={{ fontSize: '1.1875rem', fontWeight: 700, letterSpacing: '-0.02em', color: 'inherit' }}>
              {nomePlataforma}
            </Typography>
          </Box>

          <Box sx={{ position: 'relative', my: 'auto', py: 3 }}>
            <Typography
              component="h1"
              sx={{ fontSize: '1.6875rem', lineHeight: 1.18, fontWeight: 700, letterSpacing: '-0.02em', mb: 1.5, color: 'inherit' }}
            >
              Gestão municipal guiada por dados
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.55, color: 'rgba(255,255,255,.88)', maxWidth: '34ch', mb: 3.25 }}>
              {subtitulo}
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {PONTOS.map(({ icon: Icon, titulo, texto }) => (
                <Box component="li" key={titulo} sx={{ display: 'flex', gap: 1.5, fontSize: '0.8125rem', lineHeight: 1.45 }}>
                  <Box
                    sx={{
                      flex: 'none',
                      width: 32,
                      height: 32,
                      borderRadius: '9px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255,255,255,.15)',
                      border: '1px solid rgba(255,255,255,.24)',
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ color: 'rgba(255,255,255,.9)' }}>
                    <Box component="b" sx={{ color: 'white', fontWeight: 600 }}>{titulo}</Box>
                    <br />
                    {texto}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Typography
            sx={{ position: 'relative', fontSize: '0.71875rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(255,255,255,.72)' }}
          >
            Um módulo da plataforma Vivver/Crescer
          </Typography>
        </Box>

        {/* Formulário (direita) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 3, sm: 5.5 },
            py: { xs: 4, sm: 5 },
          }}
        >
          <Box sx={{ width: '100%', maxWidth: { md: 340 } }}>
            {/* Marca no mobile (o painel da esquerda some) */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              {marca(44, false)}
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: crescer.text, letterSpacing: '-0.02em' }}>
                {nomePlataforma}
              </Typography>
            </Box>

            <Typography component="h2" sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.02em', color: crescer.text, mb: 0.5 }}>
              Entrar
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: crescer.textFaint, mb: 3 }}>
              Acesse com sua conta.
            </Typography>

            {/* Erro */}
            {error && (
              <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2.5 }}>
                {error}
              </Alert>
            )}

            {/* Formulário */}
            <Box component="form" onSubmit={handleSubmit}>
              {/* E-mail */}
              <Box sx={{ mb: 1.75 }}>
                <Typography component="label" htmlFor="login-email" sx={{ ...labelSx, display: 'block' }}>
                  E-mail
                </Typography>
                <TextField
                  id="login-email"
                  required
                  fullWidth
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
                />
              </Box>

              {/* Senha */}
              <Box sx={{ mb: 1.5 }}>
                <Typography component="label" htmlFor="login-senha" sx={{ ...labelSx, display: 'block' }}>
                  Senha
                </Typography>
                <TextField
                  id="login-senha"
                  required
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          sx={{ color: crescer.textFaint }}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
                />
              </Box>

              {/* Esqueceu a senha */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: crescer.brand,
                    textDecoration: 'none',
                    '&:hover': { color: crescer.brandDark, textDecoration: 'underline' },
                  }}
                >
                  Esqueceu a senha?
                </Link>
              </Box>

              {/* Botão entrar */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ py: 1.5, fontWeight: 600, fontSize: '0.875rem' }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Entrar'}
              </Button>
            </Box>

            {/* Rodapé */}
            <Typography sx={{ color: crescer.textFaint, fontSize: '0.6875rem', mt: 4, textAlign: 'center' }}>
              &copy; {new Date().getFullYear()} {nomePlataforma}. Todos os direitos reservados.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ModernLogin;
